import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../../i18n/LanguageContext';

interface ServicePopularityItem {
  serviceName: string;
  orderCount: number;
  percentage: number;
}

interface ServicePopularityChartProps {
  data: ServicePopularityItem[];
}

const COLORS = ['#2563EB', '#059669', '#D97706', '#DB2777', '#7C3AED', '#0891B2', '#9CA3AF'];

function ServicePopularityChart({ data }: ServicePopularityChartProps) {
  const { t } = useLanguage();

  const totalOrders = data.reduce((sum, item) => sum + item.orderCount, 0);

  // Top 6, rest as "Other"
  let displayData: ServicePopularityItem[];
  if (data.length > 6) {
    const top6 = data.slice(0, 6);
    const otherCount = data.slice(6).reduce((s, i) => s + i.orderCount, 0);
    const otherPct = data.slice(6).reduce((s, i) => s + i.percentage, 0);
    displayData = [...top6, { serviceName: t('dashboard.chart.other'), orderCount: otherCount, percentage: otherPct }];
  } else {
    displayData = data;
  }

  const chartData = displayData.map((item) => ({
    name: item.serviceName,
    value: item.orderCount,
  }));

  if (data.length === 0 || totalOrders === 0) {
    return (
      <div className="partner-chart-card">
        <h3>{t('dashboard.chart.title')}</h3>
        <div className="partner-chart-empty">
          {t('dashboard.chart.empty')}
        </div>
      </div>
    );
  }

  return (
    <div className="partner-chart-card">
      <h3>{t('dashboard.chart.title')}</h3>
      <div className="partner-chart-container">
        <div className="partner-chart-donut">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="80%"
                dataKey="value"
                stroke="none"
              >
                {chartData.map((_entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="partner-chart-center">
            <div className="partner-chart-center-value">{totalOrders}</div>
            <div className="partner-chart-center-label">{t('dashboard.chart.totalOrders')}</div>
          </div>
        </div>

        <div className="partner-chart-legend">
          {displayData.map((item, index) => (
            <div key={item.serviceName} className="partner-chart-legend-item">
              <span className="partner-chart-legend-dot" style={{ background: COLORS[index % COLORS.length] }} />
              <span className="partner-chart-legend-name">{item.serviceName}</span>
              <span className="partner-chart-legend-count">{item.orderCount}</span>
              <span className="partner-chart-legend-pct">{item.percentage.toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ServicePopularityChart;
